/**
 * Speaker Verification Module — wraps @jaehyun-ko/speaker-verification.
 *
 * Provides enrollment (training), embedding extraction, and comparison
 * for the Voice Lock feature. Uses the NeXt-TDNN mobile-128 model
 * (~5 MB) running in ONNX Runtime WASM in the browser.
 *
 * This module is lazy-loaded — the ONNX model is NOT downloaded until
 * the user explicitly enables Voice Lock or starts training.
 */

import { STORAGE_KEYS } from '../utils/constants';
import { remoteLog } from '../utils/remoteLog';

// ── Storage Keys ──
// Model-keyed helper functions — each model gets its own profile
export function profileKey(model: string): string {
  return `${STORAGE_KEYS.VOICE_LOCK_PROFILE}:${model}`;
}
export function trainedAtKey(model: string): string {
  return `${STORAGE_KEYS.VOICE_LOCK_TRAINED_AT}:${model}`;
}
function embeddingsKey(model: string): string {
  return `${STORAGE_KEYS.VOICE_LOCK_EMBEDDINGS}:${model}`;
}
const MODEL_STORAGE_KEY = STORAGE_KEYS.VOICE_LOCK_MODEL;

// ── Model Config ──
const DEFAULT_MODEL_KEY = 'mobile-128';

/** Available speaker verification models. */
export interface ModelInfo {
  key: string;
  name: string;
  size: string;
  type: 'mobile' | 'standard';
}

export const SPEAKER_MODELS: ModelInfo[] = [
  { key: 'mobile-128', name: 'Mobile Compact (128ch)', size: '5 MB', type: 'mobile' },
  { key: 'mobile-256', name: 'Mobile Standard (256ch)', size: '20 MB', type: 'mobile' },
  { key: 'standard-128', name: 'Compact (128ch)', size: '7.5 MB', type: 'standard' },
  { key: 'standard-256', name: 'Standard (256ch)', size: '28 MB', type: 'standard' },
  { key: 'standard-192', name: 'Medium (192ch)', size: '16 MB', type: 'standard' },
  { key: 'standard-384', name: 'Large (384ch)', size: '32 MB', type: 'standard' },
];

/** Get the currently selected model key from localStorage, or the default. */
export function getSelectedModelKey(): string {
  return localStorage.getItem(MODEL_STORAGE_KEY) || DEFAULT_MODEL_KEY;
}

/** Save the selected model key to localStorage. */
export function setSelectedModelKey(key: string): void {
  localStorage.setItem(MODEL_STORAGE_KEY, key);
}

/**
 * Encode a Float32Array as a base64 string for localStorage storage.
 */
function encodeFloat32ToBase64(arr: Float32Array): string {
  const bytes = new Uint8Array(arr.buffer, arr.byteOffset, arr.byteLength);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Decode a base64 string back into a Float32Array.
 */
function decodeBase64ToFloat32(base64: string): Float32Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Float32Array(bytes.buffer);
}

/**
 * Average multiple Float32Array embeddings element-wise.
 */
function averageEmbeddings(embeddings: Float32Array[]): Float32Array {
  if (embeddings.length === 0) throw new Error('No embeddings to average');
  const len = embeddings[0].length;
  const result = new Float32Array(len);
  for (const emb of embeddings) {
    for (let i = 0; i < len; i++) {
      result[i] += emb[i];
    }
  }
  for (let i = 0; i < len; i++) {
    result[i] /= embeddings.length;
  }
  // L2-normalize the averaged embedding
  let norm = 0;
  for (let i = 0; i < len; i++) {
    norm += result[i] * result[i];
  }
  norm = Math.sqrt(norm);
  if (norm > 0) {
    for (let i = 0; i < len; i++) {
      result[i] /= norm;
    }
  }
  return result;
}

/**
 * Manages speaker verification lifecycle:
 * - Lazy model initialization
 * - Embedding extraction from 16kHz mono PCM
 * - Enrollment (training) with multiple samples
 * - Scoring against enrolled profile
 */
export class SpeakerVerificationManager {
  private verifier: import('@jaehyun-ko/speaker-verification').SpeakerVerification | null = null;
  private initialized = false;
  private initializing = false;
  private initPromise: Promise<void> | null = null;
  private enrolledEmbedding: Float32Array | null = null;
  private currentModelKey: string | null = null;

  /**
   * Initialize the ONNX model. Heavy — do once, cache aggressively.
   * Safe to call multiple times (idempotent). Concurrent callers await
   * the same initialization promise to avoid race conditions.
   * Reads the model key from localStorage (or uses default).
   */
  async init(): Promise<void> {
    if (this.initialized) return;
    // If already initializing, await the existing promise instead of returning
    // immediately. This prevents callers from seeing stale enrolled state.
    if (this.initializing && this.initPromise) return this.initPromise;

    this.initializing = true;
    this.initPromise = this._doInit();
    try {
      await this.initPromise;
    } finally {
      this.initPromise = null;
    }
  }

  private async _doInit(): Promise<void> {
    const modelKey = getSelectedModelKey();

    try {
      // ONNX Runtime WASM files (ort-wasm-simd-threaded.jsep.{mjs,wasm}) are
      // copied into the build output's assets/ directory by the copy-ort-wasm-assets
      // Vite plugin. The default resolution (relative to the bundled chunk URL)
      // finds them there — no CDN or wasmPaths override needed.

      remoteLog('voicelock', 'model_init_start', { model: modelKey });
      const startTime = performance.now();

      // Dynamic import to avoid loading the package at app startup
      const { SpeakerVerification } = await import('@jaehyun-ko/speaker-verification');
      this.verifier = new SpeakerVerification();
      await this.verifier.initialize(modelKey, { cacheModel: true });

      const elapsed = Math.round(performance.now() - startTime);
      remoteLog('voicelock', 'model_init_done', { model: modelKey, elapsedMs: elapsed });

      this.initialized = true;
      this.currentModelKey = modelKey;
      // Migrate old flat-key profiles (one-time) then load model-keyed profile
      this.migrateOldProfile(modelKey);
      this.loadEnrolledProfile();
    } catch (err) {
      remoteLog('voicelock', 'model_init_error', { error: String(err) });
      console.error('[SpeakerVerification] Failed to initialize:', err);
      this.verifier = null;
    } finally {
      this.initializing = false;
    }
  }

  /**
   * Switch to a different model. Profiles are per-model — switching preserves
   * the current model's profile and loads the new model's profile if one exists.
   */
  async changeModel(newModelKey: string): Promise<void> {
    if (this.currentModelKey === newModelKey && this.initialized) return;

    // Save the new model key
    setSelectedModelKey(newModelKey);

    // DON'T clear profile — profiles are now per-model
    // Tear down current model
    await this.cleanup();

    // Re-initialize with the new model (will load that model's profile if it exists)
    await this.init();
  }

  /** Get the currently loaded model key. */
  get modelKey(): string | null {
    return this.currentModelKey;
  }

  /**
   * Extract a speaker embedding from raw audio.
   * @param audio Float32Array of 16kHz mono PCM samples
   */
  async getEmbedding(audio: Float32Array): Promise<Float32Array> {
    if (!this.verifier || !this.initialized) {
      throw new Error('SpeakerVerificationManager not initialized');
    }
    const result = await this.verifier.getEmbedding(audio);
    return result.embedding;
  }

  /**
   * Compare two embeddings, returning cosine similarity in [0.0, 1.0].
   */
  compareSimilarity(a: Float32Array, b: Float32Array): number {
    if (!this.verifier || !this.initialized) {
      throw new Error('SpeakerVerificationManager not initialized');
    }
    // compareEmbeddings returns cosine similarity (dot product of L2-normalized vectors)
    // Range is [-1, 1] but for normalized speech embeddings it's typically [0, 1]
    const raw = this.verifier.compareEmbeddings(a, b);
    return Math.max(0, Math.min(1, raw));
  }

  /**
   * Enroll a voice profile from multiple audio samples.
   * Extracts embeddings from each, averages them, and saves to localStorage.
   */
  async enroll(audioSamples: Float32Array[]): Promise<void> {
    if (!this.verifier || !this.initialized) {
      throw new Error('SpeakerVerificationManager not initialized');
    }
    if (audioSamples.length === 0) {
      throw new Error('No audio samples provided for enrollment');
    }

    remoteLog('voicelock', 'enroll_start', { sampleCount: audioSamples.length });

    const embeddings: Float32Array[] = [];
    for (let i = 0; i < audioSamples.length; i++) {
      const emb = await this.getEmbedding(audioSamples[i]);
      embeddings.push(emb);
      remoteLog('voicelock', 'enroll_sample', { index: i, embLength: emb.length });
    }

    const averaged = averageEmbeddings(embeddings);
    this.enrolledEmbedding = averaged;

    // Save averaged profile to model-keyed localStorage
    const encoded = encodeFloat32ToBase64(averaged);
    localStorage.setItem(profileKey(this.currentModelKey!), encoded);
    localStorage.setItem(trainedAtKey(this.currentModelKey!), new Date().toISOString());

    // Save individual embeddings for additive retraining
    const encodedEmbeddings = embeddings.map(e => encodeFloat32ToBase64(e));
    localStorage.setItem(embeddingsKey(this.currentModelKey!), JSON.stringify(encodedEmbeddings));

    remoteLog('voicelock', 'enroll_done', {
      sampleCount: audioSamples.length,
      embLength: averaged.length,
      model: this.currentModelKey,
    });
  }

  /**
   * Enroll additional audio samples on top of an existing profile.
   * Loads existing individual embeddings, extracts new ones, combines,
   * re-averages, and saves everything.
   */
  async enrollAdditional(newAudioSamples: Float32Array[]): Promise<void> {
    if (!this.verifier || !this.initialized) {
      throw new Error('SpeakerVerificationManager not initialized');
    }
    if (newAudioSamples.length === 0) {
      throw new Error('No audio samples provided for additional enrollment');
    }

    remoteLog('voicelock', 'enroll_additional_start', { newSampleCount: newAudioSamples.length });

    // Load existing embeddings from model-keyed localStorage
    const existingEmbeddings: Float32Array[] = [];
    const storedRaw = localStorage.getItem(embeddingsKey(this.currentModelKey!));
    if (storedRaw) {
      try {
        const parsed = JSON.parse(storedRaw) as string[];
        for (const b64 of parsed) {
          existingEmbeddings.push(decodeBase64ToFloat32(b64));
        }
      } catch (err) {
        console.warn('[SpeakerVerification] Failed to decode stored embeddings, starting fresh:', err);
      }
    }

    // Extract new embeddings
    const newEmbeddings: Float32Array[] = [];
    for (let i = 0; i < newAudioSamples.length; i++) {
      const emb = await this.getEmbedding(newAudioSamples[i]);
      newEmbeddings.push(emb);
      remoteLog('voicelock', 'enroll_additional_sample', { index: i, embLength: emb.length });
    }

    // Combine old + new
    const allEmbeddings = [...existingEmbeddings, ...newEmbeddings];

    // Re-average
    const averaged = averageEmbeddings(allEmbeddings);
    this.enrolledEmbedding = averaged;

    // Save averaged profile to model-keyed localStorage
    const encoded = encodeFloat32ToBase64(averaged);
    localStorage.setItem(profileKey(this.currentModelKey!), encoded);
    localStorage.setItem(trainedAtKey(this.currentModelKey!), new Date().toISOString());

    // Save all individual embeddings
    const encodedEmbeddings = allEmbeddings.map(e => encodeFloat32ToBase64(e));
    localStorage.setItem(embeddingsKey(this.currentModelKey!), JSON.stringify(encodedEmbeddings));

    remoteLog('voicelock', 'enroll_additional_done', {
      existingCount: existingEmbeddings.length,
      newCount: newEmbeddings.length,
      totalCount: allEmbeddings.length,
      embLength: averaged.length,
    });
  }

  /**
   * Load an enrolled embedding from model-keyed localStorage.
   * @returns true if a profile was loaded, false otherwise
   */
  loadEnrolledProfile(): boolean {
    if (!this.currentModelKey) {
      this.enrolledEmbedding = null;
      return false;
    }
    const pKey = profileKey(this.currentModelKey);
    remoteLog('voicelock', 'load_profile_start', { model: this.currentModelKey, profileKey: pKey });
    const encoded = localStorage.getItem(pKey);
    remoteLog('voicelock', 'load_profile_storage', {
      profileKey: pKey,
      found: encoded !== null,
      length: encoded ? encoded.length : 0,
    });
    if (!encoded) {
      this.enrolledEmbedding = null;
      return false;
    }
    try {
      this.enrolledEmbedding = decodeBase64ToFloat32(encoded);
      remoteLog('voicelock', 'load_profile_success', { embeddingLength: this.enrolledEmbedding.length });
      return true;
    } catch (err) {
      remoteLog('voicelock', 'load_profile_error', { error: String(err) });
      console.warn('[SpeakerVerification] Failed to decode stored profile:', err);
      this.enrolledEmbedding = null;
      return false;
    }
  }

  /**
   * Score audio against the enrolled profile.
   * @returns similarity in [0.0, 1.0], or -1 if no profile is enrolled
   */
  async scoreAudio(audio: Float32Array): Promise<number> {
    if (!this.enrolledEmbedding) return -1;
    const testEmb = await this.getEmbedding(audio);
    return this.compareSimilarity(this.enrolledEmbedding, testEmb);
  }

  /** Clear the enrolled voice profile from memory and model-keyed storage. */
  clearProfile(): void {
    this.enrolledEmbedding = null;
    if (this.currentModelKey) {
      localStorage.removeItem(profileKey(this.currentModelKey));
      localStorage.removeItem(trainedAtKey(this.currentModelKey));
      localStorage.removeItem(embeddingsKey(this.currentModelKey));
    }
    remoteLog('voicelock', 'profile_cleared', { model: this.currentModelKey });
  }

  /** Check if a voice profile is currently enrolled. */
  get isEnrolled(): boolean {
    return this.enrolledEmbedding !== null;
  }

  /** Check if the model is initialized and ready. */
  get isReady(): boolean {
    return this.initialized;
  }

  /**
   * One-time migration: move old profiles to the model-keyed format.
   *
   * Migration paths (run in order):
   * 1. Device-keyed (PR #140): `voice-lock-profile:<model>:<deviceId>` → `voice-lock-profile:<model>`
   *    Scans localStorage for device-keyed keys, prefers `:default`, consolidates to model-only.
   * 2. Flat-key (v1): `voice-lock-profile` → `voice-lock-profile:<model>`
   */
  private migrateOldProfile(modelKey: string): void {
    const targetKey = profileKey(modelKey);

    // Migration path 1: device-keyed → model-only
    // Old format from PR #140: `voice-lock-profile:<model>:<deviceId>`
    // Scan localStorage for any matching keys and consolidate to model-only key
    if (!localStorage.getItem(targetKey)) {
      const prefix = `${STORAGE_KEYS.VOICE_LOCK_PROFILE}:${modelKey}:`;
      // Prefer :default if it exists, otherwise take the first match
      let sourceDeviceKey: string | null = null;
      let defaultDeviceKey: string | null = null;
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) {
          if (key === `${prefix}default`) {
            defaultDeviceKey = key;
          } else if (!sourceDeviceKey) {
            sourceDeviceKey = key;
          }
        }
      }
      const chosenKey = defaultDeviceKey || sourceDeviceKey;
      if (chosenKey) {
        const deviceSuffix = chosenKey.slice(prefix.length);
        localStorage.setItem(targetKey, localStorage.getItem(chosenKey)!);
        // Migrate trainedAt
        const deviceTrainedKey = `${STORAGE_KEYS.VOICE_LOCK_TRAINED_AT}:${modelKey}:${deviceSuffix}`;
        const deviceTrainedAt = localStorage.getItem(deviceTrainedKey);
        if (deviceTrainedAt) {
          localStorage.setItem(trainedAtKey(modelKey), deviceTrainedAt);
        }
        // Migrate embeddings
        const deviceEmbKey = `${STORAGE_KEYS.VOICE_LOCK_EMBEDDINGS}:${modelKey}:${deviceSuffix}`;
        const deviceEmb = localStorage.getItem(deviceEmbKey);
        if (deviceEmb) {
          localStorage.setItem(embeddingsKey(modelKey), deviceEmb);
        }
        // Clean up ALL device-keyed variants
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && (
            key.startsWith(`${STORAGE_KEYS.VOICE_LOCK_PROFILE}:${modelKey}:`) ||
            key.startsWith(`${STORAGE_KEYS.VOICE_LOCK_TRAINED_AT}:${modelKey}:`) ||
            key.startsWith(`${STORAGE_KEYS.VOICE_LOCK_EMBEDDINGS}:${modelKey}:`)
          )) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(k => localStorage.removeItem(k));
        remoteLog('voicelock', 'profile_migrated_device_keyed', { model: modelKey, from: chosenKey });
      }
    }

    // Migration path 2: flat-key → model-keyed
    const oldFlatProfile = localStorage.getItem(STORAGE_KEYS.VOICE_LOCK_PROFILE);
    if (oldFlatProfile && !localStorage.getItem(targetKey)) {
      localStorage.setItem(targetKey, oldFlatProfile);
      const oldTrainedAt = localStorage.getItem(STORAGE_KEYS.VOICE_LOCK_TRAINED_AT);
      if (oldTrainedAt) {
        localStorage.setItem(trainedAtKey(modelKey), oldTrainedAt);
      }
      const oldEmbeddings = localStorage.getItem(STORAGE_KEYS.VOICE_LOCK_EMBEDDINGS);
      if (oldEmbeddings) {
        localStorage.setItem(embeddingsKey(modelKey), oldEmbeddings);
      }
      localStorage.removeItem(STORAGE_KEYS.VOICE_LOCK_PROFILE);
      localStorage.removeItem(STORAGE_KEYS.VOICE_LOCK_TRAINED_AT);
      localStorage.removeItem(STORAGE_KEYS.VOICE_LOCK_EMBEDDINGS);
      remoteLog('voicelock', 'profile_migrated_flat', { model: modelKey });
    }
  }

  /** Clean up ONNX resources. */
  async cleanup(): Promise<void> {
    if (this.verifier) {
      await this.verifier.cleanup();
      this.verifier = null;
      this.initialized = false;
    }
  }
}
